import React, { useState, useEffect, useRef, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSort } from '@fortawesome/free-solid-svg-icons';
import { faCheckSquare, faSquare } from '@fortawesome/free-regular-svg-icons';

import { formatDateAsString, removeCamelCase } from '../../../util/StringUtil';
import { alphaNumericSort } from '../../../util/SortUtil';

import { Pagination } from './pagination/Pagination';
import { TableSearch } from './table-search/TableSearch';
import { TableFilter } from './table-filter/TableFilter';
import { StandardButton } from '../button/standard-button/StandardButton';
import { Checkbox } from '../checkbox/Checkbox';

import './Table.css';

export const SelectableTable = ({
  tableData,
  buttonText,
  onButtonClick,
  buttonDisabled,
  hiddenColumns,
  searchEnabled,
  filterEnabled,
  tableTitle,
  onSelectedFormsChange,
  selectedForms,
  maxRowCount,
  customHeaderName,
}) => {
  const [filteredTableData, setFilteredTableData] = useState([]);
  const [tableDataPages, setTableDataPages] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [filteredColumns, setFilteredColumns] = useState([]);

  const sortKey = useRef(null);
  const isReversed = useRef(false);

  const performInitialSort = useCallback(
    (data) => {
      if (!data || data.length === 0) return [];

      const row = data[0];
      for (const [key] of Object.entries(row)) {
        if (!hiddenColumns.includes(key)) {
          sortKey.current = key;
          break;
        }
      }

      data.sort((a, b) => alphaNumericSort(a, b, sortKey.current));
      return data;
    },
    [hiddenColumns]
  );

  useEffect(() => {
    const copy = [...tableData];
    performInitialSort(copy);
    setFilteredTableData(copy);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableData]);

  useEffect(() => {
    const copy = [...filteredTableData];
    const pages = [];
    while (copy.length) pages.push(copy.splice(0, maxRowCount));
    setTableDataPages(pages);
    setCurrentPage((p) => Math.min(p, Math.max(pages.length - 1, 0)));
  }, [filteredTableData, maxRowCount]);

  const handleHeaderClick = (key) => {
    const copy = [...filteredTableData];

    if (key !== sortKey.current) {
      sortKey.current = key;
      isReversed.current = false;
      copy.sort((a, b) => alphaNumericSort(a, b, key));
    } else {
      isReversed.current = !isReversed.current;
      copy.reverse();
    }

    setFilteredTableData(copy);
    setCurrentPage(0);
  };

  const handleRowClick = (form) => {
    form.selected = !form.selected;

    const updatedData = filteredTableData.map((item) => (item === form ? { ...form } : item));
    setFilteredTableData(updatedData);

    const selectedUserIndex = selectedForms.findIndex((user) => user.username === form.username);
    if (selectedUserIndex !== -1 && !form.selected) {
      const newSelectedForms = [...selectedForms];
      newSelectedForms[selectedUserIndex] = { ...form, selected: false };
      onSelectedFormsChange?.(newSelectedForms);
    } else {
      const newSelected = updatedData.filter((f) => f.selected);
      onSelectedFormsChange?.(newSelected);
    }
  };

  const getVisibleKeys = () => {
    if (!filteredTableData.length) return [];
    return Object.keys(filteredTableData[0]).filter((k) => !hiddenColumns.includes(k) && !filteredColumns.includes(k));
  };

  const visibleKeys = getVisibleKeys();

  const buildTableHeader = () => {
    if (!filteredTableData.length) return null;

    return (
      <thead className="table-header">
        <tr className="table-header-row">
          <th style={{ width: '110px', minWidth: '44px', maxWidth: '110px' }}>{customHeaderName}</th>

          {visibleKeys.map((key, index) => (
            <th key={index} onClick={() => handleHeaderClick(key)}>
              <div className="table-header-content">
                <div className="table-header-text">{removeCamelCase(key)}</div>
                <div className="table-header-icon">
                  <FontAwesomeIcon icon={faSort} />
                </div>
              </div>
            </th>
          ))}
        </tr>
      </thead>
    );
  };

  const buildTableBody = () => {
    const page = tableDataPages[currentPage] || [];

    return (
      <tbody className="table-body">
        {page.map((row, rowIndex) => (
          <tr key={rowIndex} className="table-body-row" onClick={() => handleRowClick(row)}>
            <td style={{ width: '44px', minWidth: '44px', maxWidth: '44px', textAlign: 'center' }}>
              <Checkbox color={'#000000'} height={'16px'} width={'16px'} checked={row.selected} />
            </td>

            {visibleKeys.map((key, cellIndex) => {
              const value = row[key];
              const text = value === null || value === undefined ? '' : value.toString();

              if (text === '') {
                return <td key={cellIndex}>N/A</td>;
              }
              if (text === 'true') {
                return (
                  <td key={cellIndex}>
                    <FontAwesomeIcon className="capture-modal-list-item-icon" icon={faCheckSquare} />
                  </td>
                );
              }
              if (text === 'false') {
                return (
                  <td key={cellIndex}>
                    <FontAwesomeIcon className="capture-modal-list-item-icon" icon={faSquare} />
                  </td>
                );
              }
              if (text.includes('00:00')) {
                return <td key={cellIndex}>{formatDateAsString(new Date(text))}</td>;
              }
              return <td key={cellIndex}>{text}</td>;
            })}
          </tr>
        ))}
      </tbody>
    );
  };

  const tableHeader = buildTableHeader();
  const tableBody = tableDataPages[currentPage] ? buildTableBody() : null;

  return (
    <div className="table-container">
      {tableTitle && (
        <div className="table-title">
          {tableTitle}
          <div className="table-header-button-container">
            {buttonText && (
              <StandardButton text={buttonText} onClick={onButtonClick} disabled={buttonDisabled} required={true} />
            )}
          </div>
        </div>
      )}

      <div className="table-control-container">
        <div className="table-search-filter-container">
          <div className="search-wrapper">
            {searchEnabled && tableData.length > 0 && (
              <TableSearch
                dataToFilter={tableData}
                isReversed={isReversed.current}
                sortKey={sortKey.current}
                setFilteredData={setFilteredTableData}
                setCurrentPage={setCurrentPage}
                hiddenColumns={hiddenColumns}
                margin={'7px'}
                searchBarWidth={'40%'}
                inputWidth={'calc(100% - 40px)'}
                searchIconAlignment={'50px'}
              />
            )}
          </div>

          {filterEnabled && (
            <div className="filter-wrapper">
              <TableFilter
                dataToFilter={tableData}
                setFilteredData={setFilteredTableData}
                hiddenColumns={hiddenColumns}
                setCurrentPage={setCurrentPage}
                filteredColumns={filteredColumns}
                setFilteredColumns={setFilteredColumns}
              />
            </div>
          )}
        </div>
      </div>

      <div style={{ overflowX: 'auto', maxWidth: '100%' }}>
        <table className="table">
          {tableHeader}
          {tableBody}
        </table>
      </div>

      {filteredTableData.length > 0 && (
        <Pagination pageCount={tableDataPages.length} currentPage={currentPage} setCurrentPage={setCurrentPage} />
      )}
    </div>
  );
};
